// src/components/ReceiptScanner.js
//
// Receipt scanner with two modes:
//
//   mode='receipt'  →  Phone 2 (Scanner Confirm) layout. Owns its own
//                       Save flow: inserts a transactions row directly.
//                       Calls onSaved() when done.
//
//   mode='bill'     →  Same scan UI, but on confirm/save it calls back
//                       to onDataExtracted(data, file, receiptUrl) so
//                       Bills.js can do the insert itself. Preserves
//                       existing Bills integration.
//
// THE SCAN-RECEIPT EDGE FUNCTION CALL, BASE64 ENCODING, STORAGE UPLOAD
// TO THE 'receipts' BUCKET, AND 50-RECEIPT TRIAL LIMIT ARE LOCKED PER
// HANDOFF — DO NOT MODIFY.
//
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { usePartner } from '../lib/usePartner'
import { theme } from '../lib/theme'
import MatchPrompt from './MatchPrompt'
import { detectMatch } from '../lib/merchantMatching'
import { dedupeCategories, resolveCategoryId, fetchHouseholdCategories } from '../lib/categories'

const TAX_OPTIONS = [
  { id: 'personal', label: 'Personal' },
  { id: 'business', label: 'Business' },
  { id: 'charity',  label: 'Charity'  },
]

export default function ReceiptScanner({
  onClose,
  onSaved,                // called on successful insert (mode='receipt')
  onDataExtracted,        // legacy callback (mode='bill' only)
  mode = 'receipt',
}) {
  const { partnerId } = usePartner()
  const isBillMode = mode === 'bill'

  // ─── Scan state ────────────────────────────────────────────
  const [scanning, setScanning] = useState(false)
  const [preview, setPreview] = useState(null)        // base64 image, 'pdf', or null
  const [imageFile, setImageFile] = useState(null)
  const [error, setError] = useState('')
  const [confirmMode, setConfirmMode] = useState(false)

  // ─── Confirm form state ────────────────────────────────────
  const [merchantName, setMerchantName] = useState('')
  const [amount, setAmount] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [dateLowConfidence, setDateLowConfidence] = useState(false) // AI said date_confidence: "low"
  const [paymentMethodId, setPaymentMethodId] = useState(null)
  const [taxStatus, setTaxStatus] = useState('personal')
  const [note, setNote] = useState('')

  // Inline-edit toggles (so tapping a field reveals an input)
  const [editing, setEditing] = useState(null)        // 'merchant' | 'amount' | null

  // Sub-views (category / payment pickers)
  const [view, setView] = useState('main')            // 'main' | 'category' | 'payment'

  // ─── Reference data for confirm view ───────────────────────
  const [categories, setCategories] = useState([])       // deduped (one per canonical name)
  const [allCategoryRows, setAllCategoryRows] = useState([]) // raw rows, for resolving old-month ids
  const [paymentMethods, setPaymentMethods] = useState([])
  const [merchantHistory, setMerchantHistory] = useState([])

  // ─── Save state ────────────────────────────────────────────
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [matchPrompt, setMatchPrompt] = useState(null) // Chunk 5: bill/debt match confirmation step

  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  // ─── Load reference data when entering confirm flow ────────
  useEffect(() => {
    if (!confirmMode || isBillMode) return
    let cancelled = false
    ;(async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user || cancelled) return
      const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

      // All household category rows (every month) deduped by canonical name —
      // see src/lib/categories.js. Previously this fell back to loading every
      // month's rows un-deduped when no current-month budget existed, which
      // rendered each category once per month of history.
      const [catData, txRes, pmRes] = await Promise.all([
        fetchHouseholdCategories(supabase, userIds),
        supabase.from('transactions')
          .select('merchant_name, category_id, payment_method_id')
          .eq('user_id', user.id)
          .order('transaction_date', { ascending: false })
          .limit(200),
        supabase.from('payment_methods').select('*').in('user_id', userIds),
      ])

      if (cancelled) return
      setAllCategoryRows(catData.rows)
      setCategories(dedupeCategories(catData.rows, { budgets: catData.budgets, userId: user.id }))
      if (pmRes.data) setPaymentMethods(pmRes.data)

      if (txRes.data) {
        // Merchant→category history for auto-categorisation after scan
        const seen = {}
        txRes.data.forEach(tx => {
          if (tx.merchant_name && !seen[tx.merchant_name.toLowerCase()]) {
            seen[tx.merchant_name.toLowerCase()] = {
              name: tx.merchant_name,
              category_id: tx.category_id,
            }
          }
        })
        setMerchantHistory(Object.values(seen))

        // Smart-default payment method — most-used by this user
        const myTxs = txRes.data.filter(t => t.payment_method_id)
        if (myTxs.length > 0) {
          const counts = {}
          myTxs.forEach(t => { counts[t.payment_method_id] = (counts[t.payment_method_id] || 0) + 1 })
          const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]
          if (top) setPaymentMethodId(top[0])
        }
      }
    })()
    return () => { cancelled = true }
  }, [confirmMode, partnerId, isBillMode])

  // After categories load, try to match the AI's merchant against history
  useEffect(() => {
    if (!confirmMode || !merchantName || categoryId || merchantHistory.length === 0) return
    const lower = merchantName.toLowerCase()
    const match = merchantHistory.find(m =>
      m.name.toLowerCase() === lower ||
      m.name.toLowerCase().includes(lower) ||
      lower.includes(m.name.toLowerCase())
    )
    // History holds an old month's category id — map it to the picker's row.
    const resolved = resolveCategoryId(match?.category_id, allCategoryRows, categories)
    if (resolved) setCategoryId(resolved)
  }, [confirmMode, merchantName, merchantHistory, categoryId, allCategoryRows, categories])

  // ─── DATA LAYER — UNTOUCHED PER HANDOFF ────────────────────

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setImageFile(file)
    const reader = new FileReader()
    reader.onload = (evt) => {
      const base64Full = evt.target.result
      // Show preview placeholder for PDFs (we can't render the data URL inline),
      // but still send the file to OCR — the Edge Function handles PDFs.
      if (file.type === 'application/pdf') {
        setPreview('pdf')
      } else {
        setPreview(base64Full)
      }
      extractData(base64Full, file)
    }
    reader.readAsDataURL(file)
  }

  const uploadReceiptImage = async (file) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null

      const { data: profile } = await supabase
        .from('profiles')
        .select('subscription_status, receipt_count')
        .eq('id', user.id)
        .single()

      if (profile?.subscription_status === 'trial' && (profile?.receipt_count || 0) >= 50) {
        setError('You\'ve reached the 50 receipt limit for trial accounts. Upgrade to Pro for unlimited receipt storage.')
        return null
      }

      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/${Date.now()}.${fileExt}`

      const { data, error: uploadErr } = await supabase.storage
        .from('receipts')
        .upload(fileName, file, { upsert: false })

      if (uploadErr) throw uploadErr

      await supabase.from('profiles')
        .update({ receipt_count: (profile?.receipt_count || 0) + 1 })
        .eq('id', user.id)

      const { data: urlData } = supabase.storage
        .from('receipts')
        .getPublicUrl(fileName)

      return urlData?.publicUrl || null

    } catch (err) {
      console.error('Upload error:', err)
      return null
    }
  }

  const extractData = async (base64Image, file) => {
    setScanning(true)
    setError('')

    try {
      const base64Data = base64Image.split(',')[1]
      const mediaType = base64Image.split(';')[0].split(':')[1]

      console.log('Calling scan-receipt function...')

      const { data: { session } } = await supabase.auth.getSession()

      const { data, error: fnError } = await supabase.functions.invoke('scan-receipt', {
        body: { base64Image: base64Data, mediaType, mode },
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : {},
      })

      console.log('scan-receipt response:', { data, fnError })

      if (fnError) throw new Error(fnError.message)

      if (data?.success && data?.data) {
        const extracted = data.data

        if (extracted.merchant_name) setMerchantName(extracted.merchant_name)
        if (extracted.name) setMerchantName(extracted.name)
        if (extracted.amount) setAmount(extracted.amount.toString())
        // Only accept ISO dates — anything else would break the date input.
        const isIso = (d) => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)
        if (isIso(extracted.date)) setDate(extracted.date)
        if (isIso(extracted.due_date)) setDate(extracted.due_date)
        // date_confidence is optional; treat a non-ISO date as low confidence too.
        setDateLowConfidence(!isBillMode && (
          extracted.date_confidence === 'low' || (extracted.date != null && !isIso(extracted.date))
        ))
        if (extracted.notes) setNote(extracted.notes)

        setScanning(false)
        setConfirmMode(true)
      } else {
        throw new Error(data?.error || 'Could not extract data')
      }

    } catch (err) {
      console.error('Extraction error:', err)
      setError('Could not read automatically. Please fill in the details below.')
      setScanning(false)
      setConfirmMode(true)
    }
  }

  // ─── Save (mode='receipt') ─────────────────────────────────
  // Owns the insert. For mode='bill', save delegates to the legacy
  // onDataExtracted callback.
  const handleSave = async () => {
    if (saving || saved) return
    if (!merchantName || !amount) return

    if (isBillMode) {
      // Legacy path — Bills.js handles the insert
      let receiptUrl = null
      const taxRequiresReceipt = taxStatus === 'business' || taxStatus === 'charity'
      if (imageFile && (taxRequiresReceipt || isBillMode)) {
        receiptUrl = await uploadReceiptImage(imageFile)
      }
      onDataExtracted(
        {
          name: merchantName,
          amount: parseFloat(amount) || null,
          due_date: date,
          notes: note,
          frequency: 'monthly',
        },
        imageFile,
        receiptUrl
      )
      return
    }

    // mode='receipt' — insert transaction directly
    if (!categoryId) return

    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

    const txMonth = new Date(date + 'T12:00:00').getMonth() + 1
    const txYear = new Date(date + 'T12:00:00').getFullYear()
    const { data: budget } = await supabase.from('budgets').select('id')
      .in('user_id', userIds).eq('month', txMonth).eq('year', txYear)
      .eq('is_active', true).order('created_at').limit(1).maybeSingle()

    // Save receipt image when business/charity (tax-time relevant)
    const isBusiness = taxStatus === 'business'
    const isCharitable = taxStatus === 'charity'
    const shouldSaveReceipt = isBusiness || isCharitable

    let receiptUrl = null
    if (imageFile && shouldSaveReceipt) {
      receiptUrl = await uploadReceiptImage(imageFile)
    }

    const num = parseFloat(amount)
    const { data: inserted } = await supabase.from('transactions').insert({
      user_id: user.id,
      budget_id: budget?.id,
      category_id: categoryId,
      amount: num,
      type: 'expense',
      merchant_name: merchantName || null,
      note: note || null,
      transaction_date: date,
      is_business: isBusiness,
      is_charitable: isCharitable,
      payment_method_id: paymentMethodId || null,
      receipt_url: receiptUrl,
    }).select('id').single()

    // ─── Auto-match-then-confirm (Chunk 5) — same flow as QuickAdd ──
    if (inserted && (merchantName || '').trim()) {
      const [{ data: billsData }, { data: debtsData }] = await Promise.all([
        supabase.from('bills').select('id, name, amount, category_id').in('user_id', userIds).eq('is_active', true),
        supabase.from('debts').select('id, name, current_balance, monthly_payment').in('user_id', userIds),
      ])
      const detection = await detectMatch({
        transaction: { id: inserted.id, merchant_name: merchantName, amount: num, transaction_date: date },
        bills: billsData || [], debts: debtsData || [], userId: user.id,
      })
      if (detection.status === 'prompt') {
        setSaving(false)
        setMatchPrompt({
          transactions: [{ id: inserted.id, amount: num, transaction_date: date }],
          kind: detection.kind, target: detection.target,
          bills: billsData || [], debts: debtsData || [], userId: user.id,
        })
        return
      }
    }

    setSaving(false)
    setSaved(true)
    setTimeout(() => { onSaved && onSaved() }, 700)
  }

  const handleRetry = () => {
    setPreview(null)
    setError('')
    setConfirmMode(false)
    setImageFile(null)
    setMerchantName('')
    setAmount('')
    setCategoryId('')
    setDate(new Date().toISOString().split('T')[0])
    setDateLowConfidence(false)
    setNote('')
    setEditing(null)
    setView('main')
  }

  // ─── Keyboard shortcuts ────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (view !== 'main') { setView('main'); return }
        if (editing) { setEditing(null); return }
        onClose()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleSave()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [view, editing, merchantName, amount, categoryId, date, paymentMethodId, taxStatus, note])

  // ─── Derived ───────────────────────────────────────────────
  const selectedCategory = categories.find(c => c.id === categoryId)
  const selectedPM = paymentMethods.find(p => p.id === paymentMethodId)
  const parentCategories = categories.filter(c => !c.parent_id)
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId)

  const formattedAmount = useMemo(() => {
    const num = parseFloat(amount)
    if (!amount || isNaN(num)) return '—'
    return `$${num.toFixed(2)}`
  }, [amount])

  const formattedDate = useMemo(() => {
    if (!date) return '—'
    try {
      return new Date(date + 'T12:00:00').toLocaleDateString('default', {
        month: 'short', day: 'numeric', year: 'numeric',
      })
    } catch { return date }
  }, [date])

  // Chunk 11 sanity check — informational only, never blocks saving (old
  // receipts are legitimate). Receipts only: bill due dates are often future.
  const showDateWarning = useMemo(() => {
    if (isBillMode || !date) return false
    if (dateLowConfidence) return true
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const d = new Date(date + 'T00:00:00')
    if (isNaN(d)) return false
    const days = Math.floor((today - d) / (1000 * 60 * 60 * 24)) // negative = future
    return days > 60 || days < 0
  }, [isBillMode, date, dateLowConfidence])

  // ─── Render: category picker sub-view ─────────────────────
  if (view === 'category') {
    return (
      <Sheet onClose={onClose}>
        <SheetHeader title="Select category" onBack={() => setView('main')} onClose={onClose} />
        <div style={styles.scrollArea}>
          {categories.length === 0 ? (
            <div style={styles.noCats}>
              No categories yet — set up your budget in Plan first.
            </div>
          ) : (
            <div style={{ padding: '8px 0 16px' }}>
              {parentCategories.map(parent => {
                const subs = getSubcategories(parent.id)
                return (
                  <div key={parent.id}>
                    <button
                      type="button"
                      className="btn-compact"
                      style={{
                        ...styles.catRow,
                        background: categoryId === parent.id ? c.greenSoft : 'transparent',
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        if (subs.length === 0) {
                          setCategoryId(parent.id)
                          setView('main')
                        }
                      }}
                      disabled={subs.length > 0}
                    >
                      <span style={styles.catName}>{parent.name}</span>
                      {subs.length > 0
                        ? <span style={{ color: c.inkQuiet, fontSize: '16px', lineHeight: 1 }} aria-hidden="true">›</span>
                        : categoryId === parent.id
                          ? <span style={{ color: c.green, fontSize: '15px', lineHeight: 1 }} aria-hidden="true">✓</span>
                          : null}
                    </button>
                    {subs.map(sub => (
                      <button
                        key={sub.id}
                        type="button"
                        className="btn-compact"
                        style={{
                          ...styles.catRow,
                          paddingLeft: '52px',
                          background: categoryId === sub.id ? c.greenSoft : 'transparent',
                        }}
                        onClick={() => {
                          setCategoryId(sub.id)
                          setView('main')
                        }}
                      >
                        <span style={{ ...styles.catName, fontSize: '14px' }}>{sub.name}</span>
                        {categoryId === sub.id && <span style={{ color: c.green, fontSize: '15px', lineHeight: 1 }} aria-hidden="true">✓</span>}
                      </button>
                    ))}
                    {subs.length > 0 && (
                      <button
                        type="button"
                        className="btn-compact"
                        style={{
                          ...styles.catRow,
                          paddingLeft: '52px',
                          background: categoryId === parent.id ? c.greenSoft : 'transparent',
                        }}
                        onClick={() => {
                          setCategoryId(parent.id)
                          setView('main')
                        }}
                      >
                        <span style={{ ...styles.catIcon, color: c.inkQuiet }}>•</span>
                        <span style={{ ...styles.catName, fontSize: '14px', color: c.inkMuted, fontStyle: 'italic' }}>
                          {parent.name} (general)
                        </span>
                        {categoryId === parent.id && <span style={{ color: c.green, fontSize: '15px', lineHeight: 1 }} aria-hidden="true">✓</span>}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Sheet>
    )
  }

  // ─── Render: payment method sub-view ──────────────────────
  if (view === 'payment') {
    return (
      <Sheet onClose={onClose}>
        <SheetHeader title="Paid with" onBack={() => setView('main')} onClose={onClose} />
        <div style={styles.scrollArea}>
          <div style={{ padding: '8px 0 16px' }}>
            <button
              type="button"
              className="btn-compact"
              style={{
                ...styles.catRow,
                background: !paymentMethodId ? c.greenSoft : 'transparent',
              }}
              onClick={() => { setPaymentMethodId(null); setView('main') }}
            >
              <span style={{ ...styles.catIcon, color: c.inkQuiet }}>—</span>
              <span style={styles.catName}>Don't track</span>
              {!paymentMethodId && <span style={{ color: c.green, fontSize: '15px', lineHeight: 1 }} aria-hidden="true">✓</span>}
            </button>
            {paymentMethods.length === 0 && (
              <div style={styles.emptyHint}>
                No payment methods yet — add them in Settings to track which card was used.
              </div>
            )}
            {paymentMethods.map(pm => (
              <button
                key={pm.id}
                type="button"
                className="btn-compact"
                style={{
                  ...styles.catRow,
                  background: paymentMethodId === pm.id ? c.greenSoft : 'transparent',
                }}
                onClick={() => { setPaymentMethodId(pm.id); setView('main') }}
              >
                <span style={styles.catName}>
                  {pm.name}{pm.last_four ? ` · ${pm.last_four}` : ''}
                </span>
                {paymentMethodId === pm.id && <span style={{ color: c.green, fontSize: '15px', lineHeight: 1 }} aria-hidden="true">✓</span>}
              </button>
            ))}
          </div>
        </div>
      </Sheet>
    )
  }

  // ─── Render: bill/debt match confirmation (Chunk 5) ───────
  if (matchPrompt) {
    return (
      <Sheet onClose={() => { setMatchPrompt(null); onSaved && onSaved() }}>
        <MatchPrompt
          merchantName={merchantName}
          transactions={matchPrompt.transactions}
          kind={matchPrompt.kind}
          target={matchPrompt.target}
          bills={matchPrompt.bills}
          debts={matchPrompt.debts}
          userId={matchPrompt.userId}
          onDone={() => { setMatchPrompt(null); onSaved && onSaved() }}
          onCancel={() => { setMatchPrompt(null); onSaved && onSaved() }}
        />
      </Sheet>
    )
  }

  // ─── Render: saved confirmation ───────────────────────────
  if (saved) {
    return (
      <Sheet onClose={onClose}>
        <div style={styles.savedState}>
          <div style={styles.savedIcon}>
            <span style={{ fontSize: '30px', lineHeight: 1 }} aria-hidden="true">✓</span>
          </div>
          <div style={styles.savedText}>Saved</div>
          <div style={styles.savedSub}>
            {formattedAmount}
            {selectedCategory ? ` · ${selectedCategory.name}` : ''}
          </div>
        </div>
      </Sheet>
    )
  }

  // ─── Render: main flow ────────────────────────────────────
  return (
    <Sheet onClose={onClose}>

      {/* Header */}
      <header style={styles.header}>
        <button
          type="button"
          className="btn-compact"
          style={styles.iconBtn}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <span style={styles.headerTitle}>
          {scanning
            ? 'Reading…'
            : confirmMode
              ? 'Confirm'
              : (isBillMode ? 'Scan bill' : 'Scan receipt')}
        </span>
        {confirmMode || preview ? (
          <button
            type="button"
            className="btn-compact"
            style={styles.iconBtn}
            onClick={handleRetry}
            aria-label="Try again"
            title="Try again"
          >
            <span aria-hidden="true" style={{ fontSize: '18px', lineHeight: 1 }}>↻</span>
          </button>
        ) : (
          <span style={{ width: '36px', height: '36px' }} aria-hidden="true" />
        )}
      </header>

      <div style={styles.scrollArea}>

        {/* Empty state */}
        {!preview && !confirmMode && (
          <div style={styles.uploadArea}>
            <div style={styles.uploadText}>
              Take a photo or upload your {isBillMode ? 'bill' : 'receipt'}.
              {' '}AI will extract the details.
            </div>
            <div style={styles.uploadButtons}>
              <button
                type="button"
                style={styles.cameraBtn}
                onClick={() => cameraInputRef.current?.click()}
              >
                Take photo
              </button>
              <button
                type="button"
                style={styles.uploadBtn}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload
              </button>
            </div>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={styles.hiddenInput}
              onChange={handleFileSelect}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              style={styles.hiddenInput}
              onChange={handleFileSelect}
            />

            <button
              type="button"
              className="btn-compact"
              style={styles.manualLink}
              onClick={() => setConfirmMode(true)}
            >
              Enter manually instead
            </button>
          </div>
        )}

        {/* Preview / scanning state — image preview */}
        {preview && preview !== 'pdf' && !confirmMode && (
          <div style={styles.previewArea}>
            <img src={preview} alt="Scanned receipt" style={styles.previewImg} />
            {scanning && (
              <div style={styles.scanningOverlay}>
                <div style={styles.scanningText}>
                  Reading your {isBillMode ? 'bill' : 'receipt'}…
                </div>
                <div style={styles.scanningSubtext}>AI is extracting the details</div>
              </div>
            )}
          </div>
        )}
        {/* Preview / scanning state — PDF placeholder */}
        {preview === 'pdf' && !confirmMode && (
          <div style={{ ...styles.previewArea, minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ color: c.inkMuted, fontSize: '14px', fontWeight: 500 }}>
              📄 PDF uploaded
            </div>
            {scanning && (
              <div style={styles.scanningOverlay}>
                <div style={styles.scanningText}>
                  Reading your {isBillMode ? 'bill' : 'receipt'}…
                </div>
                <div style={styles.scanningSubtext}>AI is extracting the details</div>
              </div>
            )}
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div style={styles.errorBanner}>
            <span style={{ flex: 1 }}>{error}</span>
            {!error.includes('limit') && (
              <button
                type="button"
                className="btn-compact"
                style={styles.errorRetry}
                onClick={handleRetry}
              >
                Try again
              </button>
            )}
          </div>
        )}

        {/* Confirm view — Phone 2 layout */}
        {confirmMode && (
          <div style={styles.confirmWrap}>

            {/* Receipt thumbnail */}
            {preview && preview !== 'pdf' && (
              <div style={styles.receiptPreviewMini}>
                <img src={preview} alt="" style={styles.receiptPreviewImg} />
                <span style={styles.receiptPreviewBadge}>
                  Receipt saved
                </span>
              </div>
            )}

            {/* AI badge */}
            {preview && (
              <div style={styles.aiBadge}>
                <span>AI extracted these details — tap any to edit</span>
              </div>
            )}

            {/* Field list */}
            <div style={styles.fieldList}>

              {/* Merchant */}
              <FieldRow
                label={isBillMode ? 'Biller' : 'Merchant'}
                value={merchantName}
                placeholder={isBillMode ? 'e.g. AT&T' : 'e.g. Kroger'}
                editing={editing === 'merchant'}
                onStartEdit={() => setEditing('merchant')}
                onCommit={() => setEditing(null)}
                onChange={setMerchantName}
              />

              {/* Amount */}
              <FieldRow
                label="Amount"
                value={amount}
                displayValue={formattedAmount}
                placeholder="0.00"
                editing={editing === 'amount'}
                onStartEdit={() => setEditing('amount')}
                onCommit={() => setEditing(null)}
                onChange={setAmount}
                inputType="number"
                inputMode="decimal"
                amountStyle
              />

              {/* Category */}
              {!isBillMode && (
                <button
                  type="button"
                  className="btn-compact"
                  style={styles.fieldRow}
                  onClick={() => setView('category')}
                >
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={styles.fieldLabel}>Category</div>
                    <div style={{
                      ...styles.fieldValue,
                      color: selectedCategory ? c.plum : c.inkQuiet,
                    }}>
                      {selectedCategory
                        ? selectedCategory.name
                        : 'Choose category'}
                    </div>
                  </div>
                  <span style={{ ...styles.fieldEdit, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">▾</span>
                </button>
              )}

              {/* Date */}
              <label style={styles.fieldRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.fieldLabel}>{isBillMode ? 'Due date' : 'Date'}</div>
                  <div style={{ position: 'relative' }}>
                    <div style={styles.fieldValue} aria-hidden="true">{formattedDate}</div>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => { setDate(e.target.value); setDateLowConfidence(false) }}
                      style={styles.dateInput}
                      aria-label={isBillMode ? 'Due date' : 'Date'}
                    />
                  </div>
                  {showDateWarning && (
                    <div style={styles.dateWarning} role="note">
                      ⚠️ Double-check this date — tap to edit if wrong
                    </div>
                  )}
                </div>
              </label>

              {/* Paid with — receipts only */}
              {!isBillMode && (
                <button
                  type="button"
                  className="btn-compact"
                  style={{ ...styles.fieldRow, borderBottom: 'none' }}
                  onClick={() => setView('payment')}
                >
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={styles.fieldLabel}>Paid with</div>
                    <div style={{
                      ...styles.fieldValue,
                      color: selectedPM ? c.plum : c.inkQuiet,
                    }}>
                      {selectedPM
                        ? `${selectedPM.name}${selectedPM.last_four ? ` · ${selectedPM.last_four}` : ''}`
                        : 'Optional'}
                    </div>
                  </div>
                  <span style={{ ...styles.fieldEdit, fontSize: '14px', lineHeight: 1 }} aria-hidden="true">▾</span>
                </button>
              )}

            </div>

            {/* Tax-status segmented control — receipts only */}
            {!isBillMode && (
              <>
                <div style={styles.taxSegment} role="radiogroup" aria-label="Tax status">
                  {TAX_OPTIONS.map(opt => {
                    const active = taxStatus === opt.id
                    const activeColor = opt.id === 'business' ? c.orange
                      : opt.id === 'charity' ? c.rose
                      : c.plum
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className="btn-compact"
                        style={{
                          ...styles.segOption,
                          ...(active ? {
                            background: c.white,
                            color: activeColor,
                            boxShadow: '0 1px 3px rgba(30, 16, 64, 0.1)',
                          } : {}),
                        }}
                        onClick={() => setTaxStatus(opt.id)}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
                <div style={styles.taxHelper}>
                  Personal: just tracked. Business or Charity: receipt saved for tax time.
                </div>
              </>
            )}

            {/* Note */}
            <textarea
              style={styles.noteInput}
              placeholder="Add a note (optional)"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            {/* Save */}
            <button
              type="button"
              style={{
                ...styles.saveBtn,
                opacity: (!merchantName || !amount || (!isBillMode && !categoryId) || saving) ? 0.4 : 1,
                cursor: (!merchantName || !amount || (!isBillMode && !categoryId) || saving) ? 'not-allowed' : 'pointer',
              }}
              onClick={handleSave}
              disabled={!merchantName || !amount || (!isBillMode && !categoryId) || saving}
            >
              {saving ? 'Saving…' : (isBillMode ? 'Add bill' : 'Save transaction')}
            </button>

            <button
              type="button"
              className="btn-compact"
              style={styles.scanAgain}
              onClick={handleRetry}
            >
              {preview ? 'Scan another receipt' : 'Take a photo instead'}
            </button>
          </div>
        )}

      </div>
    </Sheet>
  )
}

// ─────────────────────────────────────────────────────────────
// FieldRow — tappable row that becomes an inline input on tap.
// (Phone 2 "field-list" pattern: tap any field to edit.)
// ─────────────────────────────────────────────────────────────

function FieldRow({
  label,
  value,
  displayValue,
  placeholder,
  editing,
  onStartEdit,
  onCommit,
  onChange,
  inputType = 'text',
  inputMode,
  amountStyle = false,
  trailingIcon: Trailing,
}) {
  const inputRef = useRef(null)
  useEffect(() => {
    if (editing) setTimeout(() => inputRef.current?.focus(), 50)
  }, [editing])

  return (
    <div
      style={styles.fieldRow}
      onClick={() => !editing && onStartEdit && onStartEdit()}
      role="button"
      tabIndex={editing ? -1 : 0}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.fieldLabel}>{label}</div>
        {editing ? (
          <input
            ref={inputRef}
            type={inputType}
            inputMode={inputMode}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onCommit}
            onKeyDown={(e) => { if (e.key === 'Enter') onCommit() }}
            placeholder={placeholder}
            style={amountStyle ? styles.fieldInputAmount : styles.fieldInput}
            step={inputType === 'number' ? '0.01' : undefined}
          />
        ) : (
          <div style={{
            ...(amountStyle ? styles.fieldValueAmount : styles.fieldValue),
            color: value ? theme.colors.plum : theme.colors.inkQuiet,
          }}>
            {value ? (displayValue || value) : placeholder}
          </div>
        )}
      </div>
      {Trailing && <Trailing size={16} style={styles.fieldEdit} aria-hidden="true" />}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Sheet shell & header
// ─────────────────────────────────────────────────────────────

function Sheet({ children, onClose }) {
  return (
    <div
      style={styles.overlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
    >
      <div style={styles.sheet}>
        <div style={styles.sheetHandle} aria-hidden="true" />
        {children}
      </div>
    </div>
  )
}

function SheetHeader({ title, onBack, onClose }) {
  return (
    <header style={styles.header}>
      <button
        type="button"
        className="btn-compact"
        style={styles.iconBtn}
        onClick={onBack}
        aria-label="Back"
      >
        ‹
      </button>
      <span style={styles.headerTitle}>{title}</span>
      <button
        type="button"
        className="btn-compact"
        style={styles.iconBtn}
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>
    </header>
  )
}

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    overscrollBehaviorX: 'contain',
    background: 'rgba(30, 16, 64, 0.6)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 2000,
    fontFamily: f.body,
  },
  sheet: {
    overscrollBehaviorX: 'contain',
    background: c.cream,
    borderRadius: '20px 20px 0 0',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '94vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -8px 40px rgba(30, 16, 64, 0.2)',
  },
  sheetHandle: {
    width: '40px',
    height: '4px',
    borderRadius: '2px',
    background: c.cream3,
    margin: '10px auto 0',
    flexShrink: 0,
  },
  header: {
    padding: '12px 22px 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  headerTitle: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '20px',
    letterSpacing: '-0.02em',
    color: c.plum,
  },
  iconBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'rgba(30, 16, 64, 0.05)',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    color: c.plum,
    border: 'none',
    fontSize: '22px',
    lineHeight: 1,
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: 'env(safe-area-inset-bottom, 16px)',
  },

  // Empty / upload state
  uploadArea: {
    padding: '28px 22px 22px',
    textAlign: 'center',
  },
  uploadIconWrap: {
    width: '72px',
    height: '72px',
    margin: '0 auto 16px',
    borderRadius: '20px',
    background: c.cream2,
    display: 'grid',
    placeItems: 'center',
    border: `1px solid ${c.line}`,
  },
  uploadText: {
    fontSize: '14.5px',
    color: c.plumSoft,
    marginBottom: '20px',
    lineHeight: 1.55,
    maxWidth: '320px',
    margin: '0 auto 20px',
  },
  uploadButtons: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '20px',
  },
  cameraBtn: {
    padding: '12px 22px',
    background: c.plum,
    color: c.cream,
    border: 'none',
    borderRadius: '12px',
    fontFamily: f.body,
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  uploadBtn: {
    padding: '12px 22px',
    background: c.white,
    color: c.plum,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: '12px',
    fontFamily: f.body,
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  hiddenInput: { display: 'none' },
  manualLink: {
    width: '100%',
    textAlign: 'center',
    color: c.inkMuted,
    fontSize: '13px',
    fontWeight: 500,
    padding: '8px',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    fontFamily: f.body,
  },

  // Preview
  previewArea: {
    margin: '0 22px 16px',
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    background: c.cream2,
  },
  previewImg: {
    display: 'block',
    width: '100%',
    maxHeight: '320px',
    objectFit: 'contain',
    background: c.cream2,
  },
  scanningOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(30, 16, 64, 0.78)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: '#FFFFFF',
  },
  scanningSpinner: {
    color: c.greenMint,
    animation: 'spin 1.2s linear infinite',
  },
  scanningText: {
    fontSize: '16px',
    fontWeight: 600,
  },
  scanningSubtext: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.7)',
  },

  // Error
  errorBanner: {
    margin: '0 22px 14px',
    padding: '12px 14px',
    background: c.roseSoft,
    color: c.rose,
    borderRadius: '12px',
    fontSize: '13.5px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontWeight: 500,
  },
  errorRetry: {
    background: 'transparent',
    border: `1px solid ${c.rose}`,
    color: c.rose,
    borderRadius: '8px',
    padding: '5px 12px',
    fontSize: '12.5px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: f.body,
    flexShrink: 0,
  },

  // Confirm
  confirmWrap: {
    paddingBottom: '16px',
  },
  receiptPreviewMini: {
    margin: '0 22px 14px',
    height: '120px',
    borderRadius: '14px',
    overflow: 'hidden',
    position: 'relative',
    background: c.cream2,
    border: `1px solid ${c.line}`,
  },
  receiptPreviewImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  receiptPreviewBadge: {
    position: 'absolute',
    bottom: '10px',
    left: '12px',
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(6px)',
    color: '#FFFFFF',
    padding: '5px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  aiBadge: {
    margin: '0 22px 14px',
    padding: '10px 14px',
    background: c.greenSoft,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '13px',
    color: c.green,
    fontWeight: 600,
  },

  // Field list
  fieldList: {
    margin: '0 22px',
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '16px',
    overflow: 'hidden',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${c.line}`,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    gap: '12px',
    fontFamily: f.body,
    color: c.plum,
  },
  fieldLabel: {
    fontSize: '12px',
    color: c.inkMuted,
    fontWeight: 500,
    marginBottom: '4px',
    letterSpacing: '0.01em',
  },
  dateWarning: {
    fontSize: '12px',
    color: c.orange,
    fontWeight: 500,
    marginTop: '4px',
    letterSpacing: '0.01em',
    lineHeight: 1.35,
    overflowWrap: 'anywhere',
  },
  fieldValue: {
    fontSize: '15px',
    fontWeight: 600,
    color: c.plum,
  },
  fieldValueAmount: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '19px',
    color: c.plum,
    letterSpacing: '-0.02em',
  },
  fieldInput: {
    width: '100%',
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontFamily: f.body,
    fontSize: '15px',
    fontWeight: 600,
    color: c.plum,
    padding: 0,
  },
  fieldInputAmount: {
    width: '100%',
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontFamily: f.display,
    fontSize: '19px',
    fontWeight: 600,
    color: c.plum,
    letterSpacing: '-0.02em',
    padding: 0,
  },
  fieldEdit: {
    color: c.inkQuiet,
    flexShrink: 0,
  },
  // Date input is overlaid invisibly so the styled value displays cleanly
  dateInput: {
    position: 'absolute',
    inset: 0,
    opacity: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '15px',
  },

  // Tax segment
  taxSegment: {
    margin: '18px 22px 0',
    padding: '4px',
    background: c.cream3,
    borderRadius: '12px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '2px',
  },
  segOption: {
    padding: '10px 8px',
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: 600,
    color: c.inkMuted,
    borderRadius: '9px',
    cursor: 'pointer',
    transition: 'all 120ms ease',
    border: 'none',
    background: 'transparent',
    fontFamily: f.body,
  },
  taxHelper: {
    margin: '8px 22px 0',
    fontSize: '12px',
    color: c.inkMuted,
    lineHeight: 1.4,
    textAlign: 'center',
  },

  // Note
  noteInput: {
    margin: '14px 22px 0',
    width: 'calc(100% - 44px)',
    padding: '14px 16px',
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: '14px',
    fontFamily: f.body,
    fontSize: '14px',
    color: c.plum,
    resize: 'none',
    outline: 'none',
    boxSizing: 'border-box',
  },

  // Save
  saveBtn: {
    margin: '18px 22px 8px',
    width: 'calc(100% - 44px)',
    height: '52px',
    background: c.plum,
    color: c.cream,
    border: 'none',
    borderRadius: '14px',
    fontFamily: f.body,
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'transform 100ms ease, opacity 120ms ease',
  },
  scanAgain: {
    width: '100%',
    textAlign: 'center',
    color: c.inkMuted,
    fontSize: '13px',
    fontWeight: 600,
    padding: '8px',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    fontFamily: f.body,
  },

  // Sub-views
  catRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 22px',
    cursor: 'pointer',
    borderBottom: `1px solid ${c.line}`,
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    transition: 'background 120ms ease',
    fontFamily: f.body,
    color: c.plum,
  },
  catIcon: {
    fontSize: '18px',
    width: '24px',
    textAlign: 'center',
    flexShrink: 0,
    color: c.plumSoft,
  },
  catName: {
    flex: 1,
    fontSize: '15px',
    color: c.plum,
  },
  noCats: {
    margin: '20px 22px',
    padding: '14px',
    background: c.orangeSoft,
    color: c.orange,
    borderRadius: '12px',
    fontSize: '13.5px',
    fontWeight: 500,
    textAlign: 'center',
  },
  emptyHint: {
    margin: '14px 22px',
    padding: '14px',
    background: c.cream2,
    color: c.inkMuted,
    borderRadius: '12px',
    fontSize: '13px',
    textAlign: 'center',
    lineHeight: 1.5,
  },

  // Saved
  savedState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 22px 80px',
  },
  savedIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    background: c.green,
    color: '#FFFFFF',
    display: 'grid',
    placeItems: 'center',
    marginBottom: '18px',
    boxShadow: '0 6px 18px -4px rgba(22, 163, 74, 0.4)',
  },
  savedText: {
    fontFamily: f.display,
    fontSize: '28px',
    fontWeight: 600,
    color: c.plum,
    letterSpacing: '-0.02em',
  },
  savedSub: {
    marginTop: '6px',
    fontSize: '14px',
    color: c.inkMuted,
  },
}

// CSS injection for spinner keyframe
if (typeof document !== 'undefined' && !document.getElementById('mv-scanner-keyframes')) {
  const styleEl = document.createElement('style')
  styleEl.id = 'mv-scanner-keyframes'
  styleEl.textContent = '@keyframes spin { to { transform: rotate(360deg); } }'
  document.head.appendChild(styleEl)
}
